<template>
    <div class="w-full">
        <tabs ref="tabs" class="w-full" @tabSelected="tabSelected" :pre-selected-index="preSelectedTab">
            <tab :title="$t('pages.analytics.tabs.overview.tab_label')" class="mr-5">
                <auctions-performance
                    :auctions="auctions"
                    :active-auction="auction"
                    :comparison="comparison"
                    :auction-years="auctionYears"
                    :initial-sort-column="overviewSortColumn"
                    :initial-sort-direction="overviewSortDirection"
                    @go-to-details="goToDetails"
                    @sorting-changed="sortingChanged"
                    ref="performanceOverview"
                />
            </tab>
            <tab :title="$t('pages.analytics.tabs.details.tab_label')" :enabled="!!auction" class="mr-5">
                <auctions-performance-details
                    :auction="auction"
                    :merch-sales="performanceDetails?.merchSales"
                    :originLevels="originLevels"
                    :performance-details="performanceDetails"
                    :auctions="$refs.performanceOverview?.sortedAuctions"
                    ref="performanceDetails"
                    @go-to-details="goToDetails"
                />
            </tab>
            <tab :title="$t('pages.analytics.tabs.reports.tab_label')" class="mr-5">
                <auction-reports/>
            </tab>
            <template v-slot:additionalControls>
                <a :href="exportToCsvHref">
                    <button type="button" class="btn btn-primary">
                        {{$t('pages.analytics.details.tabs.buttons.export_csv')}}
                    </button>
                </a>
                <button @click="exportToPdf()" type="button" class="ml-3 btn btn-primary">
                    {{$t('pages.analytics.details.tabs.buttons.export_pdf')}}
                </button>
            </template>
        </tabs>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout';
import Pagination from '@/Shared/Pagination';
import Icon from '@/Shared/Icon';
import Status from '@/Shared/Status';
import AuctionState from "@/mixins/AuctionState";
import {SortDirections} from "@/mixins/SortDirections";
import SearchFilter from "@/Shared/SearchFilter";
import { Link } from '@inertiajs/vue2';
import Tab from '@/Shared/Tab';
import Tabs from '@/Shared/Tabs';
import AuctionsPerformance from '@/Shared/Analytics/AuctionsPerformance';
import AuctionsPerformanceDetails from '@/Shared/Analytics/AuctionsPerformanceDetails';
import "jspdf/dist/polyfills.es.js";
import { jsPDF } from "jspdf";
import AuctionReports from "./Components/AuctionReports.vue";

export default {
    metaInfo() {
        return {
            title: this.$t('pages.analytics.index.auctions')
        }
    },
    mixins: [
        AuctionState,
        SortDirections
    ],
    layout: Layout,
    components: {
        Tab,
        Tabs,
        Pagination,
        Icon,
        Status,
        SearchFilter,
        Link,
        AuctionsPerformance,
        AuctionsPerformanceDetails,
        AuctionReports
    },
    props: {
        auctions: [],
        auction: {
            type: Object,
            default: null
        },
        auctionYears: Array,
        originLevels: Array,
        performanceDetails: Object,
        comparison: {
            type: Array,
            default: function() {
                return [
                    {floor_sales:0, visitors: 0, bids_per_visitor: 0},
                    {floor_sales:0, visitors: 0, bids_per_visitor: 0}
                ]
            }
        },
        sortBy: String,
        order: String
    },
    mounted() {
        if (this.auction !== null) {
            this.activateDetails();
        }
    },
    created() {
        this.preSelectedTab = this.auction ? 1 : 0;
    },
    data() {
        return {
            selectedTab: this.$refs?.tabs?.getSelectedTab(),
            exportToCsvHref: '',
            overviewSortDirection: this.order || SortDirections.data().DESCENDING,
            overviewSortColumn: this.sortBy || 'date_start',
            preSelectedTab: 0
        }
    },
    methods: {
        activateDetails() {
            this.$refs.tabs.selectTab(1);
        },
        goToDetails(auctionId, filterDetails) {
            filterDetails = filterDetails || this.$refs.performanceOverview.getFilterValues();
            let data =  { auction: auctionId };
            if(filterDetails.aggregation) {
                data.aggregation = filterDetails.aggregation;
            }
            if(filterDetails.year) {
                data.year = filterDetails.year;
            }
            if(filterDetails.dateStart) {
                data.dateStart = filterDetails.dateStart;
            }
            if(filterDetails.dateEnd) {
                data.dateEnd = filterDetails.dateEnd;
            }
            if(this.overviewSortColumn) {
                data.sortBy = this.overviewSortColumn;
            }
            if(this.overviewSortDirection) {
                data.order = this.overviewSortDirection;
            }

            this.$inertia.visit(
                route('auctions.analytics.detail', data ),
                {
                    only: ['auction', 'originLevels', 'performanceDetails', 'sortBy', 'order']
                }
            );
        },
        tabSelected(index) {
            const query = Object.assign({},  this.route().params);
            let routeName = '';
            switch(index) {
                case 1:
                    if(this.auction) {
                        query.auction = this.auction.id;
                    }
                    routeName = 'auctions.analytics.detail';
                    this.exportToCsvHref = route('auctions.analytics.download.details', { auction: this.auction?.id });
                    break;
                default:
                    // if auction is in url remove it
                    if(this.route().params?.auction) {
                        delete query.auction;
                    }
                    routeName = 'auctions.analytics';
                    this.exportToCsvHref = route('auctions.analytics.download.overview', this.$refs?.performanceOverview.getFilterValues());
            }
            window.history.replaceState(null, '', this.route(routeName, query));
        },
        exportToPdf() {
            let main = window.document.getElementById('main');
            let selectedTab = this.$refs.tabs.getSelectedTab();
            let printPreviewIdDetails = this.$refs.performanceDetails.printPreviewId;
            let printPreviewIdOverview = this.$refs.performanceOverview.printPreviewId;

            if(main) {
                let headlinks = [];
                let printCss = '';
                for( let l of window.document.head.getElementsByTagName('link')) {
                    if(l.rel && l.rel.toLowerCase() === 'stylesheet') {
                        headlinks.push(l.href);
                        if(l.href.indexOf('print.css') >= 0) {
                            printCss = l.href;
                        }
                    }
                }

                let mywindow = window.open(window.location.href, 'print_auction_performance_details');
                mywindow.document.write('<html lang="en"><head><title></title>');
                headlinks.forEach((hl) => {
                    mywindow.document.write('<link rel="stylesheet" href="' + hl + '" type="text/css" />');
                })
                mywindow.document.write('</head><body >');
                mywindow.document.write('<style>\n' + printCss.data + '</style>');
                mywindow.document.write(main.outerHTML);
                mywindow.document.write('</body></html>');
                mywindow.document.close();
                mywindow.focus();
                let clonedMain = mywindow.document.getElementById('main');
                clonedMain.setAttribute('class', clonedMain.getAttribute('class') + ' print');
                const svgs = clonedMain.getElementsByTagName("svg");

                Array.from(svgs).forEach((node) => {
                    node.parentNode.removeChild(node);
                })

                setTimeout(function() {
                    let filename = 'report.pdf';
                    let windowWidth = 0;
                    switch(selectedTab) {
                        case 0:
                            filename = 'auction_performance_overview.pdf';
                            windowWidth = mywindow.document.getElementById(printPreviewIdOverview).clientWidth
                            break;
                        case 1:
                            filename = 'auction_performance_details_' + (this.auction?.id ? this.auction.id : 'auction') + '.pdf';
                            windowWidth = mywindow.document.getElementById(printPreviewIdDetails).clientWidth
                            break;
                        default:
                            filename = 'report.pdf';
                    }
                    const doc = new jsPDF();
                    doc.html(mywindow.document.body, {
                        callback: function (doc) {
                            doc.save(filename);
                            mywindow.close();
                        },
                        x: 10,
                        y: 10,
                        width: 180,
                        height: 297,
                        windowWidth: windowWidth
                    });
                },1000);
            }
        },
        sortingChanged(sortColumn, sortDirection) {
            this.overviewSortColumn = sortColumn;
            this.overviewSortDirection = sortDirection;
            this.$refs.performanceDetails?.setAuctions(this.$refs.performanceOverview?.sortedAuctions);
        }
    }
}
</script>
